import clsx from 'clsx';
import type { ReactNode } from 'react';
import { EmptyState, ErrorBanner, LoadingBlock } from './Feedback';

export type Column<T> = {
  key: string;
  header: ReactNode;
  render: (row: T, index: number) => ReactNode;
  /** Hide the column below this breakpoint to keep narrow screens readable. */
  hide?: 'sm' | 'md' | 'lg' | 'xl';
  align?: 'left' | 'right' | 'center';
  className?: string;
  width?: string;
};

const hideClass: Record<NonNullable<Column<unknown>['hide']>, string> = {
  sm: 'hidden sm:table-cell',
  md: 'hidden md:table-cell',
  lg: 'hidden lg:table-cell',
  xl: 'hidden xl:table-cell',
};

const alignClass = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
} as const;

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  loading = false,
  error,
  onRetry,
  emptyTitle,
  emptyDescription,
  footer,
  minWidth = '48rem',
  rowClassName,
}: {
  columns: Array<Column<T>>;
  rows: T[] | undefined;
  rowKey: (row: T, index: number) => string | number;
  onRowClick?: (row: T) => void;
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  emptyTitle?: string;
  emptyDescription?: ReactNode;
  footer?: ReactNode;
  minWidth?: string;
  rowClassName?: (row: T) => string | undefined;
}) {
  if (error) {
    return (
      <div className="p-3">
        <ErrorBanner message={error instanceof Error ? error.message : String(error)} onRetry={onRetry} />
      </div>
    );
  }
  if (loading && !rows) return <LoadingBlock />;
  if (!rows || rows.length === 0) {
    return <EmptyState title={emptyTitle ?? 'Ничего не найдено'} description={emptyDescription} />;
  }

  return (
    <div className="min-w-0">
      <div className="scroll-thin w-full overflow-x-auto">
        <table className="w-full border-collapse text-sm" style={{ minWidth }}>
          <thead>
            <tr className="border-b border-zinc-800 text-left">
              {columns.map((column) => (
                <th
                  key={column.key}
                  style={column.width ? { width: column.width } : undefined}
                  className={clsx(
                    'whitespace-nowrap px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-zinc-500',
                    column.hide ? hideClass[column.hide] : '',
                    alignClass[column.align ?? 'left'],
                  )}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr
                key={rowKey(row, index)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={clsx(
                  'border-b border-zinc-800/60 last:border-0',
                  onRowClick ? 'cursor-pointer hover:bg-zinc-800/50' : 'hover:bg-zinc-800/30',
                  rowClassName?.(row),
                )}
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={clsx(
                      'px-3 py-2 align-middle text-zinc-200',
                      column.hide ? hideClass[column.hide] : '',
                      alignClass[column.align ?? 'left'],
                      column.className,
                    )}
                  >
                    {column.render(row, index)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {footer ? <div className="border-t border-zinc-800 px-3 py-2">{footer}</div> : null}
      {loading ? (
        <div className="border-t border-zinc-800 px-3 py-1.5 text-[11px] text-zinc-500">Обновление…</div>
      ) : null}
    </div>
  );
}

export function Mono({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={clsx('tabular font-mono text-xs text-zinc-300', className)}>{children}</span>;
}
